import SectionHead from "@/components/common/SectionHead";
import Reveal from "@/components/common/Reveal";
import Pill from "@/components/common/Pill";
import Tilt from "@/components/common/Tilt";
import CountUp from "@/components/common/CountUp";
import { useLocale } from "@/i18n/useLocale";
import { landingWhyFor } from "@/content/landing";
import type { LandingWhyContent } from "@/content/types";

// Timings, fill percentages and colours are data; only the method name is copy.
const PAYOUT_ROWS: ReadonlyArray<
  readonly [key: "usdc" | "card" | "bank", time: string, fillPct: number, color: string]
> = [
  ["usdc", "2h", 88, "var(--success)"],
  ["card", "8h", 62, "var(--brand)"],
  ["bank", "22h", 42, "var(--warning)"],
];

// The rule LABEL is copy; the figure after it is a commercial term and stays
// literal here, so no translation can move a risk limit by rewriting a string.
function evalRulesFor(t: LandingWhyContent): readonly string[] {
  return [
    `${t.evalLive.rules.profitTarget}  ·  8%`,
    `${t.evalLive.rules.dailyLoss}  ·  5%`,
    `${t.evalLive.rules.maxDd}  ·  10%`,
    `${t.evalLive.rules.leverage}  ·  1:100`,
  ];
}

export default function WhyFlexFund(): JSX.Element {
  const t = landingWhyFor(useLocale());
  const evalRules = evalRulesFor(t);

  return (
    <section className="section-y relative">
      <div className="container-page">
        <SectionHead
          title={
            <>
              {t.title.line1}
              <br />
              {t.title.line2Lead}
              <span className="text-brand-gradient">{t.title.line2Highlight}</span>
              {t.title.line2Tail}
            </>
          }
          sub={t.sub}
        />

        <div className="mt-6 md:mt-8 grid gap-3 grid-cols-1 md:grid-cols-12">
          {/* 80% anchor — full-width hero card */}
          <Reveal className="md:col-span-12">
            <Tilt>
              <div className="glass-card grid items-center gap-5 md:grid-cols-[1.1fr_1fr] p-4 md:p-5">
                <div>
                  <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-fg-muted">{t.split.eyebrow}</span>
                  <div className="flex items-end gap-1.5 mt-2">
                    <span
                      className="font-display font-bold leading-[0.85] text-brand-gradient"
                      style={{ fontSize: "clamp(56px, 7vw, 96px)", letterSpacing: "-0.05em" }}
                    >
                      <CountUp to={80} duration={1600} format={(n) => `${Math.round(n)}`} />
                    </span>
                    <span
                      className="font-display font-bold leading-none text-brand-emphasis"
                      style={{ fontSize: "clamp(22px, 2.6vw, 38px)", letterSpacing: "-0.04em" }}
                    >
                      %
                    </span>
                    <span className="ml-3 mb-2 font-mono text-[11px] text-fg-muted tracking-[0.12em]">
                      {t.split.baseSplitLabel}
                    </span>
                  </div>
                  <p className="mt-2 text-fg-muted text-sm md:text-[14px] max-w-[480px]">
                    {t.split.body}
                  </p>
                </div>
                <div>
                  <div className="flex justify-between font-mono text-[11px] text-fg-dim mb-1.5">
                    <span>{t.split.yourShare}</span>
                    <span>80% → 90%</span>
                  </div>
                  <div className="h-2 bg-bg-2 rounded-full overflow-hidden border border-border">
                    <div
                      className="h-full rounded-full bar-anim"
                      style={{
                        ["--pct" as string]: "80%",
                        background:
                          "linear-gradient(90deg, rgb(var(--brand)), color-mix(in srgb, rgb(var(--brand)) 60%, transparent))",
                        boxShadow: "0 0 16px rgb(var(--brand) / 0.45)",
                      }}
                    />
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap font-mono text-[11px]">
                    <span className="px-2.5 py-1 bg-bg-2 border border-border rounded-full text-fg-muted">
                      {t.split.baseChipLabel} · 80%
                    </span>
                    <span
                      className="px-2.5 py-1 rounded-full"
                      style={{
                        background: "rgb(var(--brand) / 0.12)",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: "rgb(var(--brand) / 0.45)",
                        color: "rgb(var(--brand))",
                      }}
                    >
                      {t.split.addOnChipLabel} · 90%
                    </span>
                  </div>
                </div>
              </div>
            </Tilt>
          </Reveal>

          {/* 3-card row — payouts / execution / scaling */}
          <Reveal delay={140} className="md:col-span-4">
            <Tilt style={{ height: "100%" }}>
              <div className="glass-card glass-card-hover h-full p-4 md:p-5">
                <div className="flex items-center gap-3">
                  <span
                    className="font-display font-bold leading-none text-brand-emphasis"
                    style={{ fontSize: "clamp(24px, 2.4vw, 32px)" }}
                  >
                    &lt;24h
                  </span>
                  <Pill>{t.payouts.pill}</Pill>
                </div>
                <h3 className="text-h3 font-display font-semibold mt-2">{t.payouts.title}</h3>
                <p className="mt-1.5 text-fg-muted text-sm">
                  {t.payouts.body}
                </p>
                <div className="mt-2 flex flex-col gap-1 font-mono text-[9.5px]">
                  {PAYOUT_ROWS.map(([key, time, fill, color]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="w-[50px] text-fg-dim">{t.payouts.methods[key]}</span>
                      <span className="flex-1 h-[5px] bg-bg-2 rounded-full overflow-hidden">
                        <span
                          className="block h-full"
                          style={{
                            width: `${fill}%`,
                            background:
                              color === "var(--success)"
                                ? "rgb(var(--success))"
                                : color === "var(--brand)"
                                  ? "rgb(var(--brand))"
                                  : "rgb(var(--warning))",
                          }}
                        />
                      </span>
                      <span className="text-fg-muted">{time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Tilt>
          </Reveal>

          <Reveal delay={200} className="md:col-span-4">
            <Tilt style={{ height: "100%" }}>
              <div className="glass-card glass-card-hover h-full p-4 md:p-5">
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-fg-muted">{t.execution.eyebrow}</span>
                <h3 className="text-h3 font-display font-semibold mt-2">{t.execution.title}</h3>
                <p className="mt-1.5 text-fg-muted text-sm">
                  {t.execution.body}
                </p>
                <div className="mt-2.5 flex gap-1 items-end h-8">
                  {[40, 60, 35, 75, 55, 85, 50, 70, 62, 80, 58, 90].map((h, i) => (
                    <div
                      key={i}
                      className={`w-1.5 rounded-sm ${i % 2 ? "bg-brand-emphasis" : "bg-success"}`}
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <div className="mt-2 font-mono text-[10.5px] text-fg-dim flex justify-between">
                  <span>{t.execution.latency}</span>
                  <span>{t.execution.spread}</span>
                </div>
              </div>
            </Tilt>
          </Reveal>

          <Reveal delay={260} className="md:col-span-4">
            <Tilt style={{ height: "100%" }}>
              <div className="glass-card glass-card-hover h-full p-4 md:p-5">
                <div className="flex items-center gap-3">
                  <span
                    className="font-display font-bold leading-none text-brand-emphasis"
                    style={{ fontSize: "clamp(24px, 2.4vw, 32px)" }}
                  >
                    $1M
                  </span>
                  <Pill>{t.scaling.pill}</Pill>
                </div>
                <h3 className="text-h3 font-display font-semibold mt-2">{t.scaling.title}</h3>
                <p className="mt-1.5 text-fg-muted text-sm">
                  {t.scaling.body}
                </p>
                <div className="mt-2.5 flex items-center gap-3">
                  <svg width="44" height="44" viewBox="0 0 56 56" aria-hidden="true">
                    <circle cx="28" cy="28" r="22" fill="none" stroke="rgb(var(--bg-2))" strokeWidth="6" />
                    <circle
                      cx="28"
                      cy="28"
                      r="22"
                      fill="none"
                      stroke="rgb(var(--brand))"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray="138.2"
                      strokeDashoffset="48"
                      transform="rotate(-90 28 28)"
                    />
                  </svg>
                  <div className="font-mono text-[11px] text-fg-muted leading-relaxed">
                    <div>{t.scaling.upToPrefix} $1M</div>
                    <div className="text-fg-dim">{t.scaling.milestonePath}</div>
                  </div>
                </div>
              </div>
            </Tilt>
          </Reveal>

          {/* EVAL = LIVE — full-width row */}
          <Reveal delay={320} className="md:col-span-12">
            <Tilt>
              <div className="glass-card p-4 md:p-5">
                <div className="grid items-center gap-5 evallive-grid">
                  <div>
                    <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-fg-muted">{t.evalLive.eyebrow}</span>
                    <h3 className="text-h3 font-display font-semibold mt-2">
                      {t.evalLive.title}
                    </h3>
                    <p className="mt-1.5 text-fg-muted text-[13px] max-w-[280px]">
                      {t.evalLive.body}
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-4 md:gap-6 flex-wrap">
                    <div className="flex flex-col items-center gap-2">
                      <span className="pill pill-muted px-3 py-1.5 text-[12px]">{t.evalLive.evalPill}</span>
                      <ul className="font-mono text-[10.5px] text-fg-muted text-right list-none p-0 m-0">
                        {evalRules.map((r) => (
                          <li key={r}>{r}</li>
                        ))}
                      </ul>
                    </div>
                    <span
                      className="font-display font-bold leading-none text-brand-emphasis"
                      style={{ fontSize: 36 }}
                    >
                      =
                    </span>
                    <div className="flex flex-col items-center gap-2">
                      <span className="pill px-3 py-1.5 text-[12px]">{t.evalLive.livePill}</span>
                      <ul className="font-mono text-[10.5px] text-fg-muted text-left list-none p-0 m-0">
                        {evalRules.map((r) => (
                          <li key={r}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Tilt>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
